import * as XLSX from 'xlsx';
import { cacheGet, cacheSet } from 'services/commonQuery';
import { findClient } from 'tax/clientCompanyStore';

/**
 * 원천세 Phase-1 로컬 스토어 (mapper 전)
 * - 업로드 원자료 / 필드매핑 / 급여라인 / 세율표 / 집계 스냅샷
 * 키는 tenant + 고객사 + 귀속연월로 분리한다.
 */

const mappingCache = new Map();
const lineCache = new Map();
const uploadCache = new Map();
const aggCache = new Map();

export const WHT_MAPPING_PREFIX = 'erp.tax.whtFieldMapping';
export const WHT_LINE_PREFIX = 'erp.tax.whtPayrollLine';
export const WHT_UPLOAD_PREFIX = 'erp.tax.whtPayrollUpload';
export const WHT_AGG_PREFIX = 'erp.tax.whtAggregate';
export const WHT_RATE_KEY = 'erp.tax.whtRateTable';

export const YN_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

export const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const v = String(i + 1).padStart(2, '0');
  return { value: v, label: `${i + 1}월` };
});

/** 표준 필드 — 회사마다 다른 엑셀 헤더를 여기에 매핑 */
export const WHT_STANDARD_FIELDS = [
  { value: 'empName', label: '성명', aliases: ['성명', '이름', '사원명', '한글명', '성 명', 'name'] },
  { value: 'rrn', label: '주민등록번호', aliases: ['주민등록번호', '주민번호', '주민', 'ssn', 'jumin'] },
  { value: 'taxableIncome', label: '과세소득', aliases: ['과세소득', '과세', '급여총액', '지급액', '월급여', '급여', '소득', '지급총액'] },
  { value: 'nontaxable', label: '비과세', aliases: ['비과세', '식대', '비과세소득', '비과세액'] },
  { value: 'dependents', label: '부양가족수', aliases: ['부양가족수', '부양가족', '가족수', '공제대상', '공제인원'] },
  { value: 'empNo', label: '사번', aliases: ['사번', '사원번호', '직원번호', 'empno'] },
  { value: 'deptName', label: '부서', aliases: ['부서', '소속', '부서명'] },
  { value: 'nationalPension', label: '국민연금', aliases: ['국민연금', '연금'] },
  { value: 'healthIns', label: '건강보험', aliases: ['건강보험', '건강'] },
  { value: 'empIns', label: '고용보험', aliases: ['고용보험', '고용'] },
];

export const WHT_FIELD_OPTIONS = [
  { value: '', label: '(매핑안함)' },
  ...WHT_STANDARD_FIELDS.map((f) => ({ value: f.value, label: f.label })),
];

export function createUid(prefix = 'TW') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function payYm(year, month) {
  const y = String(year || '').replace(/\D/g, '').slice(0, 4);
  const m = String(month || '').replace(/\D/g, '').padStart(2, '0').slice(-2);
  return `${y}${m}`;
}

export function nextSortOrderBy10(rows) {
  let max = 0;
  for (const r of rows || []) {
    const n = Number(r?.sortOrder);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return Math.ceil((max + 1) / 10) * 10;
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function mappingKey(tenantCode, clientCode) {
  return `${WHT_MAPPING_PREFIX}|${tenantCode || ''}|${clientCode || ''}`;
}
function lineKey(tenantCode, clientCode, ym) {
  return `${WHT_LINE_PREFIX}|${tenantCode || ''}|${clientCode || ''}|${ym || ''}`;
}
function uploadKey(tenantCode, clientCode, ym) {
  return `${WHT_UPLOAD_PREFIX}|${tenantCode || ''}|${clientCode || ''}|${ym || ''}`;
}
function aggKey(tenantCode, clientCode, ym) {
  return `${WHT_AGG_PREFIX}|${tenantCode || ''}|${clientCode || ''}|${ym || ''}`;
}

function toNumber(v) {
  if (v == null || v === '') return 0;
  const n = Number(String(v).replace(/,/g, '').replace(/원/g, '').trim());
  return Number.isFinite(n) ? n : 0;
}

function normalizeHeader(h) {
  return String(h || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[()[\]._-]/g, '');
}

/**
 * 엑셀 헤더 → 표준필드 자동매핑 제안.
 * BE AI 연동 전 규칙기반 근사. 담당자가 확인·수정 후 확정한다.
 */
export function suggestFieldMapping(headers) {
  const used = new Set();
  return (headers || []).map((header, idx) => {
    const n = normalizeHeader(header);
    let best = { field: '', score: 0 };
    if (n) {
      for (const f of WHT_STANDARD_FIELDS) {
        if (used.has(f.value)) continue;
        for (const alias of f.aliases) {
          const a = normalizeHeader(alias);
          if (!a) continue;
          let score = 0;
          if (n === a) score = 100;
          else if (n.includes(a) || a.includes(n)) score = 80;
          if (score > best.score) best = { field: f.value, score };
        }
      }
    }
    if (best.field && best.score >= 80) used.add(best.field);
    else best = { field: '', score: 0 };
    return {
      mapUid: createUid('TM'),
      excelCol: idx,
      excelHeader: String(header || ''),
      standardField: best.field,
      confidence: best.score,
      confirmedYn: best.score >= 100 ? 'Y' : 'N',
      sortOrder: (idx + 1) * 10,
      mod: 'A',
    };
  });
}

export function emptyPayrollLine(partial = {}) {
  return {
    lineUid: '',
    empNo: '',
    empName: '',
    rrn: '',
    deptName: '',
    taxableIncome: 0,
    nontaxable: 0,
    dependents: 1,
    nationalPension: 0,
    healthIns: 0,
    empIns: 0,
    incomeTax: 0,
    localTax: 0,
    sortOrder: 10,
    mod: '',
    ...partial,
  };
}

export function readExcelAoaFromArrayBuffer(buf) {
  const wb = XLSX.read(buf, { type: 'array' });
  const name = wb.SheetNames[0];
  const ws = wb.Sheets[name];
  return XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
}

export function firstHeaderRow(aoa) {
  const rows = aoa || [];
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i] || [];
    const cells = row.map((c) => String(c || '').trim());
    if (cells.some((c) => c)) return { index: i, headers: cells };
  }
  return { index: 0, headers: [] };
}

function normalizeRrn(v) {
  const raw = String(v || '').replace(/\D/g, '');
  if (!raw) return '';
  return raw.padStart(13, '0').slice(-13);
}

export function applyMappingToAoa(aoa, mappingRows) {
  const { index, headers } = firstHeaderRow(aoa);
  const maps = (mappingRows || []).filter((m) => m.mod !== 'D' && m.standardField);
  const colByField = new Map();
  maps.forEach((m) => {
    const col =
      Number.isFinite(Number(m.excelCol)) && m.excelCol !== ''
        ? Number(m.excelCol)
        : headers.findIndex((h) => String(h) === String(m.excelHeader));
    if (col >= 0) colByField.set(m.standardField, col);
  });
  const lines = [];
  for (let r = index + 1; r < (aoa || []).length; r += 1) {
    const row = aoa[r] || [];
    const empName = String(row[colByField.get('empName')] ?? '').trim();
    const rrn = normalizeRrn(row[colByField.get('rrn')]);
    if (!empName && !rrn) continue;
    lines.push(
      emptyPayrollLine({
        lineUid: createUid('TL'),
        empNo: String(row[colByField.get('empNo')] ?? '').trim(),
        empName,
        rrn,
        deptName: String(row[colByField.get('deptName')] ?? '').trim(),
        taxableIncome: toNumber(row[colByField.get('taxableIncome')]),
        nontaxable: toNumber(row[colByField.get('nontaxable')]),
        dependents: Math.max(1, Math.round(toNumber(row[colByField.get('dependents')]) || 1)),
        nationalPension: toNumber(row[colByField.get('nationalPension')]),
        healthIns: toNumber(row[colByField.get('healthIns')]),
        empIns: toNumber(row[colByField.get('empIns')]),
        sortOrder: lines.length * 10 + 10,
        mod: 'A',
      })
    );
  }
  return lines;
}

/** 세율표 마스터 — 국세청 고시 확정 전 placeholder. 하드코딩 화면값 금지, 이 테이블만 수정 */
export const DEFAULT_RATE_TABLE = [
  { incomeFrom: 0, incomeTo: 1060000, ratePct: 0, deductionAmt: 0 },
  { incomeFrom: 1060001, incomeTo: 1500000, ratePct: 6, deductionAmt: 63600 },
  { incomeFrom: 1500001, incomeTo: 3000000, ratePct: 15, deductionAmt: 198600 },
  { incomeFrom: 3000001, incomeTo: 5000000, ratePct: 24, deductionAmt: 468600 },
  { incomeFrom: 5000001, incomeTo: 999999999, ratePct: 35, deductionAmt: 1018600 },
];

export function loadRateTable() {
  const rows = readJson(WHT_RATE_KEY, null);
  if (Array.isArray(rows) && rows.length) return rows;
  writeJson(WHT_RATE_KEY, DEFAULT_RATE_TABLE);
  return DEFAULT_RATE_TABLE;
}

function dependentCredit(dependents) {
  const n = Math.max(1, Number(dependents) || 1);
  return (n - 1) * 12500;
}

export function calcWithholdingTax(line, rateTable) {
  const table = rateTable || loadRateTable();
  const base = Math.max(0, toNumber(line.taxableIncome) - toNumber(line.nontaxable));
  const bracket = table.find((t) => base >= t.incomeFrom && base <= t.incomeTo) || table[table.length - 1];
  const raw = Math.floor((base * Number(bracket.ratePct || 0)) / 100 - Number(bracket.deductionAmt || 0));
  const incomeTax = Math.max(0, raw - dependentCredit(line.dependents));
  const localTax = Math.floor(incomeTax * 0.1);
  return { ...line, incomeTax, localTax };
}

export function loadMappings(tenantCode, clientCode) {
  const key = mappingKey(tenantCode, clientCode);
  const cached = cacheGet(mappingCache, key);
  if (cached) return cached;
  const rows = (readJson(key, []) || []).map((r) => ({ ...r, mod: '' }));
  cacheSet(mappingCache, key, rows);
  return rows;
}

export function persistMappings(tenantCode, clientCode, rows) {
  const key = mappingKey(tenantCode, clientCode);
  const next = (rows || []).filter((r) => r && r.mod !== 'D').map((r) => {
    const { mod, _tempId, ...rest } = r;
    return { ...rest, mapUid: rest.mapUid || createUid('TM'), mod: '' };
  });
  writeJson(key, next);
  cacheSet(mappingCache, key, next);
  return next;
}

export function loadLines(tenantCode, clientCode, ym) {
  const key = lineKey(tenantCode, clientCode, ym);
  const cached = cacheGet(lineCache, key);
  if (cached) return cached;
  const rows = (readJson(key, []) || []).map((r) => ({ ...r, mod: '' }));
  cacheSet(lineCache, key, rows);
  return rows;
}

export function persistLines(tenantCode, clientCode, ym, rows) {
  const key = lineKey(tenantCode, clientCode, ym);
  const next = (rows || []).filter((r) => r && r.mod !== 'D').map((r) => {
    const { mod, _tempId, ...rest } = r;
    return { ...emptyPayrollLine(), ...rest, lineUid: rest.lineUid || createUid('TL'), mod: '' };
  });
  writeJson(key, next);
  cacheSet(lineCache, key, next);
  return next;
}

export function loadUploadMeta(tenantCode, clientCode, ym) {
  const key = uploadKey(tenantCode, clientCode, ym);
  const cached = cacheGet(uploadCache, key);
  if (cached) return cached;
  const row = readJson(key, null);
  cacheSet(uploadCache, key, row);
  return row;
}

export function persistUploadMeta(tenantCode, clientCode, ym, meta) {
  const key = uploadKey(tenantCode, clientCode, ym);
  const next = { ...(meta || {}), tenantCode, clientCode, payYm: ym };
  writeJson(key, next);
  cacheSet(uploadCache, key, next);
  return next;
}

export function loadAggregate(tenantCode, clientCode, ym) {
  const key = aggKey(tenantCode, clientCode, ym);
  const cached = cacheGet(aggCache, key);
  if (cached) return cached;
  const row = readJson(key, null);
  cacheSet(aggCache, key, row);
  return row;
}

export function aggregatePayroll(tenantCode, clientCode, ym, lines) {
  const table = loadRateTable();
  const calcLines = (lines || [])
    .filter((r) => r && r.mod !== 'D')
    .map((r) => calcWithholdingTax(r, table));
  const count = calcLines.length;
  const totalPay = calcLines.reduce((s, r) => s + toNumber(r.taxableIncome), 0);
  const totalNontax = calcLines.reduce((s, r) => s + toNumber(r.nontaxable), 0);
  const totalIncomeTax = calcLines.reduce((s, r) => s + toNumber(r.incomeTax), 0);
  const totalLocalTax = calcLines.reduce((s, r) => s + toNumber(r.localTax), 0);
  const client = findClient(tenantCode, clientCode);
  const snap = {
    tenantCode,
    clientCode,
    clientName: client?.clientName || '',
    bizNo: client?.bizNo || '',
    ceoName: client?.ceoName || '',
    address: client?.address || '',
    taxOffice: client?.taxOffice || '',
    payYm: ym,
    incomeKind: '근로소득(간이세액)',
    count,
    totalPay,
    totalNontax,
    totalIncomeTax,
    totalLocalTax,
    totalTax: totalIncomeTax + totalLocalTax,
    calculatedAt: new Date().toISOString(),
  };
  const key = aggKey(tenantCode, clientCode, ym);
  writeJson(key, snap);
  cacheSet(aggCache, key, snap);
  persistLines(tenantCode, clientCode, ym, calcLines);
  return { snapshot: snap, lines: calcLines };
}

export function downloadPayrollTemplate(kind = 'A') {
  const samples = {
    A: [
      ['성명', '주민등록번호', '과세소득', '비과세', '부양가족수', '부서'],
      ['김근로', '8001011234567', 3200000, 200000, 2, '생산'],
      ['이급여', '9002022345678', 2100000, 100000, 1, '관리'],
    ],
    B: [
      ['이름', '주민번호', '급여총액', '식대', '가족수', '사번'],
      ['박유통', '8503031234567', 2800000, 150000, 3, 'B-11'],
      ['최영업', '9204042345678', 1900000, 100000, 1, 'B-12'],
    ],
  };
  const aoa = samples[kind] || samples.A;
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '급여대장');
  const name = kind === 'B' ? '급여대장_샘플B(다른양식).xlsx' : '급여대장_샘플A.xlsx';
  XLSX.writeFile(wb, name);
}

function pad(v, len) {
  return String(v ?? '').slice(0, len).padEnd(len, ' ');
}

function padNum(v, len) {
  const n = Math.max(0, Math.round(toNumber(v)));
  return String(n).slice(0, len).padStart(len, '0');
}

/**
 * 전산매체 초안 파일. 국세청 확정 레코드 규격 확보 전 UTF-8 고정폭 스텁.
 */
export function buildEdiText({ snapshot, lines }) {
  const s = snapshot || {};
  const header = [
    'A',
    pad(s.payYm, 6),
    pad(String(s.bizNo || '').replace(/\D/g, ''), 10),
    pad(s.clientName, 30),
    pad(s.ceoName, 20),
  ].join('');
  const body = (lines || []).map((r) =>
    [
      'B',
      pad(normalizeRrn(r.rrn), 13),
      pad(r.empName, 20),
      padNum(r.taxableIncome, 13),
      padNum(r.nontaxable, 13),
      padNum(r.incomeTax, 13),
      padNum(r.localTax, 13),
    ].join('')
  );
  const footer = [
    'C',
    padNum(s.count, 6),
    padNum(s.totalPay, 15),
    padNum(s.totalIncomeTax, 15),
    padNum(s.totalLocalTax, 15),
  ].join('');
  const note = [
    '# 원천세 전산매체 초안 (국세청 레코드 규격 확정 전)',
    `# 고객사=${s.clientName || ''} 귀속=${s.payYm || ''} 작성=${s.calculatedAt || ''}`,
    '# 레코드: A(제출자) B(소득자) C(합계) — 홈택스 업로드는 사람이 수행',
  ].join('\n');
  return `${note}\n${header}\n${body.join('\n')}\n${footer}\n`;
}

export function downloadEdiFile({ snapshot, lines }) {
  const text = buildEdiText({ snapshot, lines });
  const ym = snapshot?.payYm || '000000';
  const biz = String(snapshot?.bizNo || '').replace(/\D/g, '') || 'biz';
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `WHT_${biz}_${ym}.txt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function formatAmt(v) {
  const n = toNumber(v);
  return n.toLocaleString('ko-KR');
}
